/**
 * «Золотая середина» между следованием камеры и правом человека посмотреть
 * карту руками.
 *
 * Задача: во время ведения камера едет за позицией и крутится по курсу. Но
 * человек в этот же момент может отодвинуть карту пальцем — заглянуть, что за
 * поворотом, найти вход, посмотреть соседний корпус. Если камера продолжит
 * центрироваться, она отдёрнет карту обратно через полсекунды, и посмотреть
 * будет физически невозможно.
 *
 * Как это решено в навигаторах (и почему здесь так же):
 *  – жест ВСЕГДА главнее автоматики: любой сдвиг/зум/поворот пальцем
 *    немедленно останавливает следование (Google Maps, Apple Maps, Waze,
 *    Organic Maps — везде одинаково);
 *  – возврат обязан быть виден и достижим одним касанием: показываем кнопку
 *    «Вернуться к себе», а не прячем выход в жест;
 *  – возврат сам по себе, если человек просто забыл: у Waze авто-возврат
 *    ~10 с, у Apple Maps ~8–10 с, у Google — кнопка плюс возврат при
 *    длительном покое. Слишком короткий таймер (2–5 с) не даёт дочитать
 *    подпись и ощущается как вырывание карты из рук; слишком длинный (30 с+)
 *    оставляет человека без своей позиции, когда он уже идёт дальше.
 *
 * Отсюда середина: RESUME_MS = 12 с — «успеть прочитать и решить», но не
 * «потерять себя». Отсчёт идёт от ПОСЛЕДНЕГО касания карты, а не от первого:
 * пока человек возит пальцем, таймер не тикает.
 *
 * Важное: удержание останавливает только КАМЕРУ. Точка «вы здесь», маршрут,
 * шаги и подсказки продолжают обновляться — человек видит, как его метка
 * едет по карте, просто карта под ней не прыгает.
 */
import { cameraHeld, lastMapGestureMs } from '../state'

/** Сколько ждать после последнего касания карты, прежде чем вернуть камеру. */
export const CAMERA_RESUME_MS = 12_000

/**
 * Пора ли возвращать камеру: покой длиннее паузы. Чистая функция —
 * тестируется без таймеров и карты.
 */
export function shouldResume(
  nowMs: number,
  lastGestureMs: number,
  resumeMs = CAMERA_RESUME_MS,
): boolean {
  return nowMs - lastGestureMs >= resumeMs
}

let timer: ReturnType<typeof setTimeout> | null = null

function clearTimer(): void {
  if (timer !== null) {
    clearTimeout(timer)
    timer = null
  }
}

/**
 * Человек тронул карту пальцем: камера отдаёт управление и ждёт покоя.
 * Повторные касания сдвигают срок возврата — таймер перезапускается.
 */
export function holdCamera(): void {
  lastMapGestureMs.value = Date.now()
  cameraHeld.value = true
  clearTimer()
  timer = setTimeout(() => {
    // Проверка на случай, если касание пришло в последний момент: тогда
    // ждём ещё, а не дёргаем карту у человека из-под пальца.
    if (shouldResume(Date.now(), lastMapGestureMs.peek())) releaseCamera()
    else holdAgainAfterGesture()
  }, CAMERA_RESUME_MS)
}

/** Дождаться остатка паузы после позднего касания. */
function holdAgainAfterGesture(): void {
  clearTimer()
  const left = Math.max(0, CAMERA_RESUME_MS - (Date.now() - lastMapGestureMs.peek()))
  timer = setTimeout(() => releaseCamera(), left)
}

/** Вернуть камеру следованию: кнопкой «Вернуться» или по истечении покоя. */
export function releaseCamera(): void {
  clearTimer()
  cameraHeld.value = false
}

/** Ведение закончилось (или не начиналось) — состояние удержания сбрасывается. */
export function resetCameraHold(): void {
  clearTimer()
  cameraHeld.value = false
  lastMapGestureMs.value = 0
}
